import { motion } from "framer-motion";
import { Instagram, Facebook, Twitter, Linkedin, Youtube, Apple, Play, ArrowUpRight } from "lucide-react";
import { Logo } from "./Logo";
import { Reveal } from "./Reveal";

const socials = [
  { icon: Instagram, label: "Instagram", href: "https://instagram.com" },
  { icon: Facebook, label: "Facebook", href: "https://facebook.com" },
  { icon: Twitter, label: "X", href: "https://twitter.com" },
  { icon: Linkedin, label: "LinkedIn", href: "https://linkedin.com" },
  { icon: Youtube, label: "YouTube", href: "https://youtube.com" },
];

const columns = [
  {
    title: "Company",
    links: ["About Us", "The Brightspot Network", "Careers", "Press"],
  },
  {
    title: "For Sellers",
    links: ["Become a Brightspot", "Partner Support", "Seller Login", "Success Stories"],
  },
  {
    title: "Help",
    links: ["FAQ", "Contact Us", "Track Order", "Refund Policy"],
  },
];

const anchorFor = (label) => {
  const map = {
    "Become a Brightspot": "#become-brightspot",
    "The Brightspot Network": "#brightspot",
    FAQ: "#faq",
  };
  return map[label] || "#";
};

export const Footer = () => {
  return (
    <footer
      id="download"
      data-testid="site-footer"
      className="relative overflow-hidden border-t border-brand-line pt-24"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-12">
        {/* Download CTA */}
        <Reveal>
          <div className="flex flex-col items-start justify-between gap-8 rounded-3xl border border-brand-line bg-brand-surface p-8 md:flex-row md:items-center md:p-12">
            <div>
              <h3 className="font-display text-4xl uppercase leading-none tracking-tight md:text-5xl">
                Bring your neighborhood
                <br />
                <span className="text-brand-volt">to your doorstep.</span>
              </h3>
              <p className="mt-4 max-w-md text-brand-muted">
                Download the Zeroomile app and discover the Brightspots around you.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <a
                href="#"
                data-testid="footer-google-play"
                className="flex items-center gap-3 rounded-2xl bg-brand-volt px-5 py-3 text-brand-bg transition-colors duration-300 hover:bg-brand-volt-hover"
              >
                <Play size={22} fill="currentColor" />
                <span className="text-left">
                  <span className="block text-[10px] uppercase tracking-widest opacity-80">Get it on</span>
                  <span className="block font-semibold leading-tight">Google Play</span>
                </span>
              </a>
              <a
                href="#"
                data-testid="footer-app-store"
                className="flex items-center gap-3 rounded-2xl border border-brand-line px-5 py-3 text-brand-bone transition-colors duration-300 hover:border-brand-volt"
              >
                <Apple size={24} />
                <span className="text-left">
                  <span className="block text-[10px] uppercase tracking-widest text-brand-muted">Download on the</span>
                  <span className="block font-semibold leading-tight">App Store</span>
                </span>
              </a>
            </div>
          </div>
        </Reveal>

        {/* Link columns */}
        <div className="mt-20 grid grid-cols-2 gap-10 md:grid-cols-5">
          <div className="col-span-2">
            <Logo markSize={34} textClass="text-3xl" />
            <p className="mt-5 max-w-xs text-sm leading-relaxed text-brand-muted">
              Hyper-local delivery that empowers local sellers through the Brightspot network.
              Every neighborhood store, a beacon of service.
            </p>
            <div className="mt-6 flex gap-3">
              {socials.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  data-testid={`social-${s.label.toLowerCase()}`}
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-brand-line text-brand-muted transition-colors duration-300 hover:border-brand-volt hover:text-brand-volt"
                >
                  <s.icon size={17} />
                </a>
              ))}
            </div>
          </div>

          {columns.map((col) => (
            <div key={col.title}>
              <h4 className="text-xs uppercase tracking-[0.2em] text-brand-bone">{col.title}</h4>
              <ul className="mt-5 space-y-3">
                {col.links.map((l) => (
                  <li key={l}>
                    <a
                      href={anchorFor(l)}
                      onClick={(e) => {
                        const target = anchorFor(l);
                        if (target.startsWith("#") && target.length > 1) {
                          e.preventDefault();
                          const el = document.getElementById(target.slice(1));
                          if (el && window.__lenis) window.__lenis.scrollTo(el, { offset: -40 });
                          else if (el) el.scrollIntoView({ behavior: "smooth" });
                        }
                      }}
                      className="group inline-flex items-center gap-1 text-sm text-brand-muted transition-colors duration-300 hover:text-brand-volt"
                    >
                      {l}
                      <ArrowUpRight
                        size={13}
                        className="opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                      />
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* legal */}
        <div className="mt-16 flex flex-col items-center justify-between gap-4 border-t border-brand-line py-8 md:flex-row">
          <p className="text-xs text-brand-muted">
            © {new Date().getFullYear()} Zeroomile. All rights reserved.
          </p>
          <div className="flex gap-6 text-xs text-brand-muted">
            <a href="#" className="transition-colors hover:text-brand-bone">Privacy Policy</a>
            <a href="#" className="transition-colors hover:text-brand-bone">Terms of Service</a>
            <a href="#" className="transition-colors hover:text-brand-bone">Cookies</a>
          </div>
        </div>
      </div>

      {/* Colossal wordmark */}
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
        className="mt-10 select-none px-2 text-center leading-none"
      >
        <span className="font-display text-[24vw] uppercase leading-[0.8] tracking-tight text-brand-elevated">
          Zeroo<span className="text-brand-volt/50">&#8734;</span>mile
        </span>
      </motion.div>
    </footer>
  );
};
